import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BoxDocumentViewerService } from '@lib/components/document-viewer/document-viewer-panel/document-viewer-panel.service';
import {
  AddArianeDocumentViewerComponent,
  AddArianeResult,
} from '@lib/components/pdf-viewer/add-ariane/add-ariane-document-viewer.component';
import {
  AddUploadDocumentViewerComponent,
  AddUploadResult,
} from '@lib/components/pdf-viewer/add-upload/add-upload-document-viewer.component';
import {
  FichierOperation,
  Investisseur,
  NatureDocumentRequi,
  Operation,
  OutdatedOperationDocument,
  TypeSignaturePartenaire,
} from '@lib/models/generated/graphql';
import { LocalFileInfo } from '@lib/models/LocalFileInfo';
import { MissingFichierOperation, isMissingFichierOperation } from '@lib/models/types';
import { DocumentsService } from '@lib/services/documents.service';
import { fileLabel } from '@lib/utils/fichierOperationLabel';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { DraftOperationsService } from 'src/service/draft-operation/draft-operation.service';
import { AddLocalFileResult } from './souscription-file-item/souscription-file-item.component';

export type missingFileInfoState = 'notMissing' | 'mandatory' | 'facultative';
export interface FileInfo {
  fichierOperation?: FichierOperation | MissingFichierOperation;
  comment?: string;
  denomination?: string;
  fileName: string;
  label: string;
  missingState: missingFileInfoState;
  isUploading?: boolean;
  outdatedDocument?: OutdatedOperationDocument;
}

export interface SelectedFileConfig {
  fileId: string;
  natureDocumentRequisId?: number;
  comment?: string;
  denomination?: string;
  isOutOfConfig: boolean;
  investisseurId: number;
  forceInAllOperations: boolean;
}

export interface UploadingFile {
  isUploading: boolean;
  fileName: string;
  typeLibelle?: string;
  comment?: string;
  denomination?: string;
}

export const DEFAULT_ID_FOR_MISSING_DOC = -1;

@Component({
    selector: 'app-souscription-file-items',
    templateUrl: './souscription-file-items.component.html',
    styleUrls: ['./souscription-file-items.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SouscriptionFileItemsComponent {
  @Input()
  itemsCategory: 'operation' | 'investisseur' | 'co-investisseur' | 'conseil' = 'operation';

  @Input()
  operation: Operation;

  @Input()
  operationIndex: number;

  @Input()
  set fichierOperations(value: (FichierOperation | MissingFichierOperation)[]) {
    this.updateFileInformationFromFichierOperation(value ?? []);
  }

  @Input()
  investisseur?: Investisseur;

  @Input()
  withGlobalAddFilesButton: boolean = false;

  @Input()
  showBannerConnaissanceClient: boolean = false;

  @Input()
  showBannerSouscription: boolean = false;

  uploadingFiles: UploadingFile[] = [];

  uploadProgress = 0;

  @Output()
  onAddedFile: EventEmitter<SelectedFileConfig[]> = new EventEmitter<SelectedFileConfig[]>();
  @Output()
  onRemoveFile: EventEmitter<FichierOperation> = new EventEmitter<FichierOperation>();

  fileInfos: FileInfo[] = [];
  outdatedDocuments: OutdatedOperationDocument[] = [];

  uploadInProgress = false;

  uploadIndex: number | undefined;
  isGlobalUpload: boolean = false;
  isGlobalUploadInProgress: boolean = false;
  categoryNatureDocumentRequiId: number;
  investisseurs: Investisseur[] = [];

  readonly defaultBannerSouscriptionTitle =
    "Merci d'ajouter tous les documents de l'opération (bulletins, justificatifs). La liste ci-dessous n'est pas nécessairement exhaustive.";

  readonly extranetSignatureBannerSouscriptionTitle = `En complément des documents listés ci-dessous, merci d'ajouter tous les documents de l'opération et que vous n’avez pas déjà déposés sur l’extranet partenaire. Si vous disposez du bulletin d'opération signé électroniquement chez le partenaire, vous pouvez le déposer au niveau de son emplacement dédié ("Facultatif")`;

  bannerSouscriptionTitle: string = this.defaultBannerSouscriptionTitle;

  globalSelectedFilesControl = new FormControl<File[] | null>(null);

  constructor(
    private dialog: MatDialog,
    private documentService: DocumentsService,
    protected draftOperationService: DraftOperationsService,
    private snackbarService: OuiSnackbarService,
    private documentViewerService: BoxDocumentViewerService
  ) {}

  ngOnInit() {
    this.buildInvestisseurs();
    if (
      this.showBannerSouscription &&
      this.operation.typeSignaturePartenaire === TypeSignaturePartenaire.ElectroniqueExtranetPartenaire
    ) {
      this.bannerSouscriptionTitle = this.extranetSignatureBannerSouscriptionTitle;
    }
    this.loadOutdatedOperationDocuments();
  }
  buildInvestisseurs() {
    this.investisseurs = [];
    if (this.draftOperationService.investisseur) {
      this.investisseurs.push(this.draftOperationService.investisseur);
    }
    if (this.draftOperationService.coInvestisseur) {
      this.investisseurs.push(this.draftOperationService.coInvestisseur);
    }
  }
  createFileInfo(fileOperation: FichierOperation | MissingFichierOperation): FileInfo {
    if (isMissingFichierOperation(fileOperation)) {
      return {
        fichierOperation: fileOperation,
        fileName: '',
        label: fileOperation.natureDocumentRequis?.natureDocument?.nom ?? 'Autre Document',
        missingState: fileOperation.isFacultative ? 'facultative' : 'mandatory',
        isUploading: false,
      };
    } else {
      return {
        fichierOperation: fileOperation,
        fileName: fileOperation?.metadata?.fileName ?? '',
        label: fileLabel(fileOperation),
        missingState: 'notMissing',
        isUploading: false,
        outdatedDocument: this.getOutdatedDocument(fileOperation),
      };
    }
  }

  sortFileInfoByMissingState(fileInfos: FileInfo[]): FileInfo[] {
    return fileInfos.sort((a, b) => {
      const order = {
        notMissing: 0,
        mandatory: 1,
        facultative: 2,
      };

      return order[a.missingState] - order[b.missingState];
    });
  }
  updateFileInformationFromFichierOperation(fichierOperations: (FichierOperation | MissingFichierOperation)[]) {
    //remove file info from previous fichier operation
    this.fileInfos = this.fileInfos.filter(fi => fi.fichierOperation === undefined);

    //add new files Info
    this.fileInfos.push(...fichierOperations.map(fo => this.createFileInfo(fo)));
    this.applyOutdatedDocumentsToFileInfos();
    this.fileInfos = this.sortFileInfoByMissingState(this.fileInfos);
    this.isGlobalUploadInProgress = false;

    // On page reload, operationGroupData can arrive after ngOnInit; reload outdated docs when file rows are ready.
    void this.loadOutdatedOperationDocuments();
  }

  async loadOutdatedOperationDocuments() {
    if (!this.draftOperationService.operationGroupData?.id) {
      return;
    }

    this.outdatedDocuments = await this.draftOperationService.getOutdatedOperationDocuments();
    this.fileInfos.forEach(fileInfo => {
      if (fileInfo.fichierOperation && !isMissingFichierOperation(fileInfo.fichierOperation)) {
        fileInfo.outdatedDocument = this.outdatedDocuments.find(
          doc => doc.fichierOperationId === fileInfo.fichierOperation?.id
        );
      }
    });
  }

  private applyOutdatedDocumentsToFileInfos() {
    this.fileInfos.forEach(fileInfo => {
      if (fileInfo.fichierOperation && !isMissingFichierOperation(fileInfo.fichierOperation)) {
        fileInfo.outdatedDocument = this.getOutdatedDocument(fileInfo.fichierOperation);
      }
    });
  }

  private getOutdatedDocument(
    fichierOperation: FichierOperation | MissingFichierOperation
  ): OutdatedOperationDocument | undefined {
    if (isMissingFichierOperation(fichierOperation)) {
      return undefined;
    }

    return this.outdatedDocuments.find(doc => doc.fichierOperationId === fichierOperation.id);
  }

  onFileSelected(event: any) {
    const file: File = event.target.files[0];
  }

  displayFile(file: FileInfo) {
    if (file.fichierOperation && !isMissingFichierOperation(file.fichierOperation)) {
      this.documentViewerService.openAppendedDocumentViewer(
        file.fichierOperation,
        result => {
          if (result?.fichierOperation?.id && typeof result.comment === 'string') {
            this.draftOperationService.updateFichierOperationCommentaire(
              result.fichierOperation?.id,
              result.comment ?? ''
            );
          }
        },
        async removeEvent => {
          if (removeEvent.fichierOperationToRemove?.id) {
            await this.draftOperationService.removeFichierOperationToCurrentGroup(removeEvent.fichierOperationToRemove);
            this.onRemoveFile.emit();
          }
        }
      );
    }
  }

  async refreshOutdatedDocument(file: FileInfo, event?: Event) {
    event?.stopPropagation();
    if (!file.fichierOperation || isMissingFichierOperation(file.fichierOperation) || !file.outdatedDocument) {
      return;
    }

    file.isUploading = true;
    try {
      const refreshedFiles = await this.draftOperationService.refreshOperationDocuments([file.fichierOperation.id]);
      const refreshedFile = refreshedFiles.find(refreshed => refreshed.id === file.fichierOperation?.id);
      if (refreshedFile) {
        file.fichierOperation = refreshedFile;
        file.fileName = refreshedFile.metadata?.fileName ?? file.fileName;
      }
      await this.loadOutdatedOperationDocuments();
      this.snackbarService.open('Le document a été mis à jour avec la dernière version GED.', 'success', 5000, {
        horizontal: 'left',
        vertical: 'bottom',
      });
    } finally {
      file.isUploading = false;
    }
  }

  async onQuickUpload(index: number, payload: AddLocalFileResult) {
    this.uploadIndex = index;
    this.isGlobalUpload = false;

    const ndrMaybe = this.fileInfos[index]?.fichierOperation?.natureDocumentRequis;
    const ndr = ndrMaybe === null ? undefined : ndrMaybe;
    await this.uploadLocalFilesAndEmit(payload.files, ndr, payload.applyToAllOperations);
  }

  private async uploadLocalFilesAndEmit(
    files: File[],
    natureDocumentRequis?: NatureDocumentRequi,
    forAllOperations = false
  ) {
    if (!files?.length) return;

    if (!files.every(f => this.documentService.isAuthorizedConsultantFile(f))) {
      this.snackbarService.open('Format ou poids de fichier non autorisé.', 'error', 5000, {
        horizontal: 'left',
        vertical: 'bottom',
      });
      return;
    }

    this.displaySpinner();

    const investisseurId = this.investisseur?.id ?? this.draftOperationService.investisseur?.id;

    const fileIds = await Promise.all(
      files.map(async f => {
        if (!investisseurId) return undefined;
        const fileId = await this.documentService.uploadFile(
          f,
          investisseurId,
          natureDocumentRequis?.natureDocument?.boxDocumentType,
          natureDocumentRequis?.natureDocument?.boxDocumentType?.key ?? f.name
        );
        return fileId;
      })
    );

    if (investisseurId && fileIds?.length) {
      await this.draftOperationService.refreshInvestisseurFilesData(investisseurId);
      this.buildInvestisseurs();
    }

    const ndrForFile = natureDocumentRequis?.id ?? this.categoryNatureDocumentRequiId;

    const outputData: SelectedFileConfig[] = (fileIds ?? [])
      .filter((id): id is string => !!id)
      .map(id => ({
        fileId: id,
        natureDocumentRequisId: ndrForFile,
        denomination: natureDocumentRequis?.natureDocument?.boxDocumentType?.key,
        comment: undefined, // no viewer => no comment
        isOutOfConfig: ndrForFile === this.categoryNatureDocumentRequiId,
        investisseurId: this.investisseurIdAccordingToCategory() ?? investisseurId,
        forceInAllOperations: forAllOperations,
      }));

    this.resetAddFilesClicked();
    this.onAddedFile.emit(outputData);
  }

  async onAddFromInvestisseurDocs(index: number) {
    this.uploadIndex = index;
    this.isGlobalUpload = false;
    const ndrMaybe = this.fileInfos[index]?.fichierOperation?.natureDocumentRequis;
    this.openArianeDialog(ndrMaybe === null ? undefined : ndrMaybe);
  }

  private openArianeDialog(natureDocumentRequis?: NatureDocumentRequi) {
    this.buildInvestisseurs();
    const dialogRef = this.dialog.open(AddArianeDocumentViewerComponent, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%',
      panelClass: 'full-screen-modal',
      data: {
        investisseurs: this.investisseurs,
        natureDocumentRequis,
        multiple: this.isGlobalUpload,
        showAllOperationsOption:
          this.draftOperationService.operationGroupData.operations.length > 1 && this.itemsCategory === 'operation',
      },
    });
    dialogRef.afterClosed().subscribe((resultData: AddArianeResult) => {
      //if a file has been selected, add create a fichier Operation

      const ndrForFile = natureDocumentRequis?.id ?? this.categoryNatureDocumentRequiId;
      const fileInfoList = resultData?.filesInfo;
      if (fileInfoList && Array.isArray(fileInfoList) && fileInfoList.length > 0) {
        this.displaySpinner();
        const outputData: SelectedFileConfig[] = fileInfoList.map(fi => ({
          fileId: fi.metadata.fileId!,
          natureDocumentRequisId: ndrForFile,
          investisseurId: this.investisseurIdAccordingToCategory() ?? fi.metadata.investisseurId,
          comment: fi.comment,
          isOutOfConfig: ndrForFile === this.categoryNatureDocumentRequiId,
          forceInAllOperations: !!resultData.forAllOperations,
        }));
        this.resetAddFilesClicked();
        this.onAddedFile.emit(outputData);
      }
    });
  }

  async onQuickGlobalUpload(files: File[] | null) {
    if (!files?.length) return;

    if (!files.every(f => this.documentService.isAuthorizedConsultantFile(f))) {
      this.snackbarService.open('Format ou poids de fichier non autorisé.', 'error', 5000, {
        horizontal: 'left',
        vertical: 'bottom',
      });
      return;
    }

    this.uploadIndex = undefined;
    this.isGlobalUpload = true;

    const dialogRef = this.dialog.open(AddUploadDocumentViewerComponent, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%',
      panelClass: 'full-screen-modal',
      autoFocus: false,
      data: {
        files,
        natureDocumentRequi: undefined,
        typeSelectable: true,
        investisseurs: this.investisseurs,
        multiple: this.isGlobalUpload,
        showAllOperationsOption:
          this.draftOperationService.operationGroupData.operations.length > 1 && this.itemsCategory === 'operation',
      },
    });

    this.globalSelectedFilesControl.setValue([]);

    dialogRef.afterClosed().subscribe(async (result: AddUploadResult) => {
      if (!Array.isArray(result?.filesInfo) || result.filesInfo.length === 0) return;

      const localFileInfos: LocalFileInfo[] = result.filesInfo;
      const fileIdToFileInfo = new Map<string, LocalFileInfo>();

      this.displaySpinner();

      try {
        const investisseurId =
          localFileInfos[0].investisseurId ?? this.investisseur?.id ?? this.draftOperationService.investisseur?.id;

        const fileIds = await Promise.all(
          localFileInfos.map(async lfi => {
            if (!investisseurId) return undefined;
            const fileId = await this.documentService.uploadFile(
              lfi.file,
              investisseurId,
              lfi.boxDocumentType,
              lfi.denomination
            );
            if (fileId) fileIdToFileInfo.set(fileId, lfi);
            return fileId;
          })
        );

        const uploaded = fileIds.filter((id): id is string => !!id);
        if (investisseurId && uploaded.length) {
          await this.draftOperationService.refreshInvestisseurFilesData(investisseurId);
          this.buildInvestisseurs();
        }

        const ndrForFile = this.categoryNatureDocumentRequiId;
        const outputData: SelectedFileConfig[] = uploaded.map(fileId => ({
          fileId,
          natureDocumentRequisId: ndrForFile,
          denomination: fileIdToFileInfo.get(fileId)?.boxDocumentType?.key,
          comment: fileIdToFileInfo.get(fileId)?.comment,
          isOutOfConfig: true,
          investisseurId: this.investisseurIdAccordingToCategory() ?? investisseurId,
          forceInAllOperations: !!result.forAllOperations,
        }));

        this.resetAddFilesClicked();
        this.onAddedFile.emit(outputData);
      } finally {
        this.isGlobalUploadInProgress = false;
      }
    });
  }

  onAddFromInvestorDocsGlobal() {
    this.uploadIndex = undefined;
    this.isGlobalUpload = true;

    this.buildInvestisseurs();
    const dialogRef = this.dialog.open(AddArianeDocumentViewerComponent, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%',
      panelClass: 'full-screen-modal',
      data: {
        investisseurs: this.investisseurs,
        natureDocumentRequis: undefined,
        multiple: this.isGlobalUpload,
        showAllOperationsOption:
          this.draftOperationService.operationGroupData.operations.length > 1 && this.itemsCategory === 'operation',
      },
    });

    dialogRef.afterClosed().subscribe((resultData: AddArianeResult) => {
      const fileInfoList = resultData?.filesInfo;
      if (fileInfoList && Array.isArray(fileInfoList) && fileInfoList.length > 0) {
        this.displaySpinner();
        const ndrId = this.categoryNatureDocumentRequiId;
        const outputData: SelectedFileConfig[] = fileInfoList.map(fi => ({
          fileId: fi.metadata.fileId!,
          natureDocumentRequisId: ndrId,
          investisseurId: this.investisseurIdAccordingToCategory() ?? fi.metadata.investisseurId,
          comment: fi.comment,
          isOutOfConfig: true,
          forceInAllOperations: !!resultData.forAllOperations,
        }));
        this.resetAddFilesClicked();
        this.onAddedFile.emit(outputData);
      }
    });
  }

  // display the spinner in the corresponding uploading file or on the global upload
  displaySpinner() {
    if (this.uploadIndex !== undefined && this.fileInfos[this.uploadIndex]) {
      this.fileInfos[this.uploadIndex].isUploading = true;
    }
    if (this.isGlobalUpload) {
      this.isGlobalUploadInProgress = true;
    }
  }
  resetAddFilesClicked() {
    this.uploadIndex = undefined;
    this.isGlobalUpload = false;
  }

  investisseurIdAccordingToCategory(): number | undefined {
    switch (this.itemsCategory) {
      case 'investisseur':
        return this.draftOperationService.investisseur?.id;
      case 'co-investisseur':
        return this.draftOperationService.coInvestisseur?.id;
      case 'conseil':
        return this.draftOperationService.investisseur?.id;
      default:
        // for operation we check that we clicked on a required file
        if (this.uploadIndex !== undefined && this.uploadIndex < this.fileInfos.length) {
          return this.fileInfos[this.uploadIndex].fichierOperation?.estCoInvestisseur
            ? this.draftOperationService.coInvestisseur?.id
            : this.draftOperationService.investisseur?.id;
        }
        return undefined;
    }
  }
}
